import { useState, useEffect, useCallback, useRef } from 'react';

interface PerformanceMetrics {
  fps: number;
  renderTime: number;
  memoryUsage: number;
  isLowPerformance: boolean;
}

interface PerformanceConfig {
  targetFPS: number;
  memoryThreshold: number; // MB
  renderThreshold: number; // ms
  adaptiveMode: boolean;
}

export type PerformanceMode = 'high' | 'medium' | 'low';

const defaultConfig: PerformanceConfig = {
  targetFPS: 60,
  memoryThreshold: 100,
  renderThreshold: 16.67, // 60fps = 16.67ms per frame
  adaptiveMode: true,
};

export const usePerformanceOptimization = (config: Partial<PerformanceConfig> = {}) => {
  const fullConfig = { ...defaultConfig, ...config };
  
  const [performanceMode, setPerformanceMode] = useState<PerformanceMode>('high');
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    renderTime: 16,
    memoryUsage: 0,
    isLowPerformance: false,
  });
  
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const renderTimesRef = useRef<number[]>([]);
  const animationFrameRef = useRef<number | null>(null);

  // Detect initial performance capabilities
  const detectInitialPerformance = useCallback((): PerformanceMode => {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isLowEndMobile = /Android.*Chrome\/[3-5][0-9]/i.test(userAgent);
    const hardwareConcurrency = navigator.hardwareConcurrency || 2;
    const deviceMemory = (navigator as any).deviceMemory || 4;
    const effectiveType = (navigator as any).connection?.effectiveType;
    
    // Check for performance indicators
    const indicators = {
      isMobile,
      isLowEndMobile,
      hasLowCores: hardwareConcurrency <= 4,
      hasLowMemory: deviceMemory <= 4,
      hasSlowConnection: effectiveType === '2g' || effectiveType === 'slow-2g',
      isSmallScreen: window.innerWidth < 768,
      hasReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    };
    
    const lowPerformanceCount = Object.values(indicators).filter(Boolean).length;
    
    if (lowPerformanceCount >= 3 || indicators.isLowEndMobile) return 'low';
    if (lowPerformanceCount >= 2 || indicators.isMobile) return 'medium';
    return 'high';
  }, []);

  // Monitor FPS and render performance
  const monitorPerformance = useCallback(() => {
    const measureFrame = (timestamp: number) => {
      frameCountRef.current++;
      const renderStart = performance.now();
      
      // Simulate minimal render work to measure
      const delta = timestamp - lastTimeRef.current;
      
      if (delta >= 1000) { // Update every second
        const fps = Math.round((frameCountRef.current * 1000) / delta);
        const avgRenderTime = renderTimesRef.current.length 
          ? renderTimesRef.current.reduce((a, b) => a + b, 0) / renderTimesRef.current.length
          : 16;
        
        // Get memory usage if available
        let memoryUsage = 0;
        if ('memory' in performance) {
          const memInfo = (performance as any).memory;
          memoryUsage = Math.round(memInfo.usedJSHeapSize / 1048576); // Convert to MB
        }
        
        const isLowPerformance = fps < fullConfig.targetFPS * 0.8 || 
                               avgRenderTime > fullConfig.renderThreshold * 1.5 ||
                               memoryUsage > fullConfig.memoryThreshold;
        
        setMetrics({
          fps,
          renderTime: avgRenderTime,
          memoryUsage,
          isLowPerformance,
        });
        
        // Adaptive performance mode adjustment
        if (fullConfig.adaptiveMode) {
          if (isLowPerformance && performanceMode !== 'low') {
            setPerformanceMode(prev => prev === 'high' ? 'medium' : 'low');
          } else if (!isLowPerformance && fps > fullConfig.targetFPS * 0.95) {
            setPerformanceMode(prev => prev === 'low' ? 'medium' : prev === 'medium' ? 'high' : prev);
          }
        }
        
        // Reset counters
        frameCountRef.current = 0;
        lastTimeRef.current = timestamp;
        renderTimesRef.current = [];
      }
      
      const renderEnd = performance.now();
      const renderTime = renderEnd - renderStart;
      
      // Keep only recent render times
      renderTimesRef.current.push(renderTime);
      if (renderTimesRef.current.length > 60) {
        renderTimesRef.current.shift();
      }
      
      animationFrameRef.current = requestAnimationFrame(measureFrame);
    };
    
    animationFrameRef.current = requestAnimationFrame(measureFrame);
  }, [fullConfig, performanceMode]);

  // Initialize performance monitoring
  useEffect(() => {
    const initialMode = detectInitialPerformance();
    setPerformanceMode(initialMode);
    
    if (fullConfig.adaptiveMode) {
      monitorPerformance();
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [detectInitialPerformance, monitorPerformance, fullConfig.adaptiveMode]);

  // Responsive performance adjustment
  useEffect(() => {
    const handleResize = () => {
      const newMode = detectInitialPerformance();
      setPerformanceMode(newMode);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [detectInitialPerformance]);

  // Performance optimization utilities
  const optimizeAnimation = useCallback((animationIntensity: 'low' | 'medium' | 'high') => {
    const intensityMap = {
      low: { high: true, medium: true, low: true },
      medium: { high: true, medium: true, low: false },
      high: { high: true, medium: false, low: false },
    };
    
    return intensityMap[animationIntensity][performanceMode];
  }, [performanceMode]);

  const getOptimizedConfig = useCallback(() => {
    const configs = {
      high: {
        particleCount: 20,
        animationDuration: 1,
        blurIntensity: 8,
        shadowIntensity: 1,
        enableParallax: true,
        enable3D: true,
        enableGlow: true,
        maxFrameRate: 60,
      },
      medium: {
        particleCount: 10,
        animationDuration: 1.5,
        blurIntensity: 4,
        shadowIntensity: 0.7,
        enableParallax: true,
        enable3D: true,
        enableGlow: false,
        maxFrameRate: 30,
      },
      low: {
        particleCount: 0,
        animationDuration: 2,
        blurIntensity: 2,
        shadowIntensity: 0.3,
        enableParallax: false,
        enable3D: false,
        enableGlow: false,
        maxFrameRate: 30,
      },
    };
    
    return configs[performanceMode];
  }, [performanceMode]);

  const throttleCallback = useCallback((callback: Function, delay: number = 16) => {
    let timeoutId: number | null = null;
    let lastExecTime = 0;
    
    return (...args: any[]) => {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        callback(...args);
        lastExecTime = currentTime;
      } else {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          callback(...args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }, []);

  const debounceCallback = useCallback((callback: Function, delay: number = 300) => {
    let timeoutId: number | null = null;
    
    return (...args: any[]) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), delay);
    };
  }, []);

  // Memory management utilities
  const cleanupResources = useCallback(() => {
    // Force garbage collection if available
    if ('gc' in window && typeof (window as any).gc === 'function') {
      (window as any).gc();
    }
    
    // Cancel any pending animation frames
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const isFeatureEnabled = useCallback((feature: 'particles' | 'blur' | 'shadows' | '3d' | 'parallax' | 'glow') => {
    const config = getOptimizedConfig();
    
    const featureMap = {
      particles: config.particleCount > 0,
      blur: config.blurIntensity > 0,
      shadows: config.shadowIntensity > 0,
      '3d': config.enable3D,
      parallax: config.enableParallax,
      glow: config.enableGlow,
    };
    
    return featureMap[feature];
  }, [getOptimizedConfig]);

  return {
    performanceMode,
    metrics,
    optimizeAnimation,
    getOptimizedConfig,
    throttleCallback,
    debounceCallback,
    cleanupResources,
    isFeatureEnabled,
    setPerformanceMode, // Manual override
  };
};

export default usePerformanceOptimization; 